'use client';

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Pin, PinOff, ArrowRight, Link as LinkIcon } from 'lucide-react';

export interface ContextMenuTarget {
  /** Pin/unpin uses this href as the key. */
  href: string;
  /** Display label (used in copy-link toast confirmation, accessibility). */
  label: string;
  /** Group title for the footer hint. */
  groupTitle?: string;
}

export interface ContextMenuPosition {
  x: number;
  y: number;
  target: ContextMenuTarget;
}

interface Props {
  position: ContextMenuPosition | null;
  isPinned: boolean;
  onTogglePin: (href: string) => void;
  onOpen: (href: string) => void;
  onClose: () => void;
}

const MENU_WIDTH = 220;
const MENU_HEIGHT_APPROX = 160;

/**
 * Right-click menu for sidebar items (pinned rows + domain tiles).
 * Items: Pin/Unpin · Open · Copy link.
 * Closes on outside click, ESC, scroll, or selection.
 */
export function NavV2ContextMenu({ position, isPinned, onTogglePin, onOpen, onClose }: Props) {
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Stable ref for onClose. Bugbot Phase 2 finding: both callers
  // (domain-tiles.tsx + pinned-list.tsx) pass `onClose={() => setMenu(null)}`
  // as an inline arrow. That handler is a new function ref every parent
  // render. If the effect's deps array included `onClose` directly, any
  // parent re-render while the menu is open would tear-down + re-setup the
  // effect, which would re-focus the first menuitem mid-arrow-navigation
  // and the user would lose their focus position.
  //
  // Fix: capture the latest `onClose` into a ref. The setup effect depends
  // ONLY on `position` (which is stable until the menu is dismissed). The
  // handlers call `onCloseRef.current()` so they always see the latest
  // close handler without the effect needing to re-run.
  //
  // Structurally robust — future callers cannot regress by passing an
  // un-memoised onClose; the menu owns the stability.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!position) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onCloseRef.current();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Bugbot Phase 2 finding: when the context menu opens inside the
        // mobile drawer (which itself listens for ESC on `document` to
        // close), the menu's ESC handler also fires — without stopping
        // propagation, both close together and the user loses the
        // drawer when they only intended to close the menu.
        //
        // Fix: this handler runs in CAPTURE phase on `document` (see
        // `{ capture: true }` registration below), so it fires BEFORE
        // any bubble-phase listener (the drawer registers in bubble).
        // `stopPropagation` then prevents the drawer's handler from
        // running. `preventDefault` blocks the default ESC behaviour
        // (e.g., browser-level dialog close).
        e.stopPropagation();
        e.preventDefault();
        onCloseRef.current();
      }
    };
    const handleScroll = () => onCloseRef.current();
    window.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKey, { capture: true });
    window.addEventListener('scroll', handleScroll, true);
    // Focus the first menu item so arrow-key navigation works immediately
    // per the WCAG APG menu pattern. This must fire ONCE per menu open
    // (when `position` transitions from null → non-null), not on every
    // parent re-render — otherwise arrow-key focus is reset mid-nav.
    const first = menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]');
    first?.focus();
    return () => {
      window.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKey, { capture: true });
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [position]);

  // Arrow-key navigation between menu items (WCAG APG menu pattern).
  // ArrowDown / ArrowUp wrap; Home / End jump to first / last.
  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;
    const activeEl = document.activeElement as HTMLElement | null;
    const currentIdx = items.findIndex((el) => el === activeEl);
    let nextIdx: number | null = null;
    switch (e.key) {
      case 'ArrowDown':
        nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % items.length;
        break;
      case 'ArrowUp':
        nextIdx = currentIdx <= 0 ? items.length - 1 : currentIdx - 1;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = items.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    if (nextIdx !== null) items[nextIdx]?.focus();
  };

  if (!position || typeof window === 'undefined') return null;

  // Clamp to viewport
  const left = Math.min(position.x, window.innerWidth - MENU_WIDTH - 8);
  const top = Math.min(position.y, window.innerHeight - MENU_HEIGHT_APPROX - 8);

  const handleCopyLink = async () => {
    try {
      const url = new URL(position.target.href, window.location.origin).toString();
      await navigator.clipboard?.writeText(url);
    } catch {
      // Silently fail — clipboard may be unavailable
    }
    onClose();
  };

  // z-[90] on the menu: it must sit above the domain-tile flyout Popover
  // (zIndex 85, itself raised above the mobile drawer's z-70). At z-[80] the
  // right-click menu painted underneath the open flyout.
  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      aria-label={`Actions for ${position.target.label}`}
      style={{ position: 'fixed', top, left, width: MENU_WIDTH }}
      className="ds-shell-navigation z-[90] rounded-overlay border border-control-border/30 bg-surface-sheet p-1.5 shadow-menu focus:outline-none"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={handleMenuKeyDown}
    >
      <MenuItem
        icon={isPinned ? PinOff : Pin}
        label={isPinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
        onClick={() => {
          onTogglePin(position.target.href);
          onClose();
        }}
      />
      <MenuItem
        icon={ArrowRight}
        label="Open"
        onClick={() => {
          onOpen(position.target.href);
          onClose();
        }}
      />
      <MenuItem icon={LinkIcon} label="Copy link" onClick={handleCopyLink} />
      {position.target.groupTitle && (
        <>
          <div className="my-1 h-px bg-surface-soft" />
          <p className="px-2.5 py-1 text-caption text-text-muted">{position.target.groupTitle}</p>
        </>
      )}
    </div>,
    document.body,
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Pin;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-left text-body text-text-heading hover:bg-surface-canvas focus:bg-surface-canvas focus:outline-none"
    >
      <Icon className="h-3.5 w-3.5 text-selected-foreground" aria-hidden="true" />
      {label}
    </button>
  );
}
